import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Download, Users, Calendar, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/user-stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load admin statistics');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadUserData = async () => {
    try {
      const response = await axios.get(`${API}/admin/export-users`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const filename = `journey_to_wealth_users_${new Date().toISOString().split('T')[0]}.csv`;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('User data exported successfully');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to export user data');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50 to-purple-100 flex items-center justify-center">
        <div className="text-xl text-purple-600">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50 to-purple-100">
      <div className="container mx-auto py-8 px-4 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-violet-700 bg-clip-text text-transparent mb-4">
            Journey To Wealth - Admin Dashboard
          </h1>
          <p className="text-lg text-gray-600">User Analytics & Data Export</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Total Users
              </CardTitle>
              <Users className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-800">
                {stats?.total_users || 0}
              </div>
              <p className="text-xs text-gray-500">
                All time registrations
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Recent Users (30 days)
              </CardTitle>
              <Calendar className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-800">
                {stats?.recent_users_30_days || 0}
              </div>
              <p className="text-xs text-gray-500">
                Last 30 days
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">
                Growth Rate
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-800">
                {stats?.total_users > 0 
                  ? Math.round((stats?.recent_users_30_days / stats?.total_users) * 100)
                  : 0}%
              </div>
              <p className="text-xs text-gray-500">
                Monthly growth
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Export Section */}
        <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Download className="w-6 h-6 text-purple-600" />
              Data Export
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-800">Export User Database</h3>
                <p className="text-sm text-gray-600">
                  Download a CSV file containing all user names, emails, and submission dates
                </p>
              </div>
              <Button
                onClick={downloadUserData}
                className="bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg transition-all duration-200"
              >
                <Download className="w-4 h-4 mr-2" />
                Download CSV
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recent Submissions */}
        <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-xl">Recent User Submissions</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_submissions?.length > 0 ? (
              <div className="space-y-4">
                {stats.recent_submissions.map((submission, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg border">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Name</p>
                        <p className="text-sm text-gray-800">{submission.name}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Email</p>
                        <p className="text-sm text-gray-800">{submission.email}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Tickers</p>
                        <p className="text-sm text-gray-800">
                          {submission.tickers_analyzed?.join(', ') || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Date</p>
                        <p className="text-sm text-gray-800">{formatDate(submission.submission_date)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No user submissions yet
              </div>
            )}
          </CardContent>
        </Card>

        {/* Back to App */}
        <div className="text-center mt-8">
          <Button
            onClick={() => window.location.href = '/'}
            variant="outline"
            className="border-purple-200 text-purple-600 hover:bg-purple-50"
          >
            ← Back to Portfolio Analysis
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;